/**
 * RequisitesDeactivateModal - Модальное окно деактивации/восстановления реквизитов
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Textarea,
  Button,
  Group,
  Stack,
  Text,
  Alert,
  Box,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconX,
  IconCheck,
  IconBuildingBank,
  IconUser,
} from '@tabler/icons-react';
import tokens from '../../../theme/designTokens';

const RequisitesDeactivateModal = ({
  opened,
  onClose,
  onConfirm,
  requisites,
  mode = 'deactivate', // 'deactivate' или 'restore'
  loading = false,
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);

  // Сброс при открытии
  useEffect(() => {
    if (opened) {
      setReason('');
      setError(null);
    }
  }, [opened]);

  if (!requisites) return null;

  const isDeactivate = mode === 'deactivate';
  const isLegal = requisites.entity_type === 'legal';

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('Укажите причину');
      return;
    }

    setError(null);
    try {
      await onConfirm(reason.trim());
    } catch (err) {
      setError(err.message || 'Произошла ошибка');
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isDeactivate ? 'Деактивировать реквизиты' : 'Восстановить реквизиты'}
      size="md"
      radius="xl"
      styles={{
        content: {
          borderRadius: '16px',
        },
      }}
    >
      <Stack gap="md">
        {/* Информация о реквизитах */}
        <Group
          p="sm"
          style={{
            borderRadius: '12px',
            backgroundColor: tokens.colors.neutral[1],
          }}
        >
          <Box
            style={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              backgroundColor: isLegal ? '#e3f2fd' : '#fff3e0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isLegal ? (
              <IconBuildingBank size={20} color="#1976d2" />
            ) : (
              <IconUser size={20} color="#e65100" />
            )}
          </Box>
          <Box style={{ flex: 1 }}>
            <Text size="sm" fw={500}>
              {requisites.company_name}
            </Text>
            <Text size="xs" c="dimmed">
              ИНН: {requisites.inn}
              {requisites.kpp && ` • КПП: ${requisites.kpp}`}
            </Text>
          </Box>
        </Group>

        {/* Предупреждение */}
        <Alert
          icon={isDeactivate ? <IconX size={16} /> : <IconCheck size={16} />}
          color={isDeactivate ? 'orange' : 'green'}
          variant="light"
        >
          {isDeactivate ? (
            <>
              <Text size="sm" fw={500}>
                Реквизиты будут помечены как неиспользуемые
              </Text>
              <Text size="xs" mt={4}>
                Они останутся в системе, но не будут отображаться как активные.
                {requisites.is_primary && ' Флаг "Основные" будет снят.'}
              </Text>
            </>
          ) : (
            <>
              <Text size="sm" fw={500}>
                Реквизиты будут восстановлены
              </Text>
              <Text size="xs" mt={4}>
                Они снова станут активными и доступными для использования.
              </Text>
            </>
          )}
        </Alert>

        {/* Причина */}
        <Textarea
          label={isDeactivate ? 'Причина деактивации' : 'Причина восстановления'}
          placeholder={
            isDeactivate
              ? 'Например: Компания закрыта, сменились реквизиты...'
              : 'Например: Ошибочная деактивация, реквизиты снова актуальны...'
          }
          required
          minRows={10}
          autosize
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={error}
        />

        {/* Кнопки */}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Отмена
          </Button>
          <Button
            color={isDeactivate ? 'orange' : 'green'}
            leftSection={isDeactivate ? <IconX size={16} /> : <IconCheck size={16} />}
            onClick={handleSubmit}
            loading={loading}
          >
            {isDeactivate ? 'Деактивировать' : 'Восстановить'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default RequisitesDeactivateModal;
